import { groupBy, pick } from "lodash";

import PromiseStatuses from "../../../../enums/promiseStatuses";
import DataService from "../../../Application/services/dataServices/typedDataService";

import { type GenericFiltersMap } from "../../../../utils/filterUtils";
import { type AccountBase, type PackName } from "../../../../interfaces";
import { ScheduleFiltersEnum } from "../../types";

import ObjectUtils from "../../../../utils/objectUtils";
import { type GoalOptions } from "features/Library/Flows/types/models";
import { type FlowTagsWithType } from "features/Library/Flows/types";
import { TagTypes } from "features/Library/Flows/types/flowBase";
import { TagsEnum } from "interfaces/TagsEnum";
import PriorityLevels from "../../../../enums/priorityLevels";
import { ScheduleStatusEnum } from "../../../../components/contentAssignment/schedules/scheduleStatusEnum";

export enum FiltersToFetch {
  Publishers,
  Tags,
  Packs,
  Goals,
  Priorities,
  Statuses,
}

export interface GetFilterOptionsProps {
  filters?: FiltersToFetch[];
}

interface BaseFilterOptionsProps {
  begin: () => void;
  success: (filterOptions: GenericFiltersMap<string>) => void;
  failure: (error: Error) => void;
}

const defaultFiltersOptions = [
  FiltersToFetch.Publishers,
  FiltersToFetch.Tags,
  FiltersToFetch.Packs,
  FiltersToFetch.Goals,
  FiltersToFetch.Priorities,
  FiltersToFetch.Statuses,
];

const tagToFilterMap: { [key in TagTypes]?: ScheduleFiltersEnum } = {
  [TagTypes.Label]: ScheduleFiltersEnum.Labels,
  [TagTypes.SoftwareApplication]: ScheduleFiltersEnum.SoftwareApplications,
};

const readPublishersData = async () => {
  const publishers = await DataService.flows.getFlowPublishers();
  const filterOptions = {
    [ScheduleFiltersEnum.Publishers]: publishers.data.map((item: AccountBase) => ({
      text: item.name,
      value: item.id,
    })),
  };
  return filterOptions;
};

const readPacksData = async () => {
  const packs = await DataService.packs.getPacksFilterOptions("flow", true);

  const filterOptions = {
    [ScheduleFiltersEnum.Packs]: packs.data.map((item: PackName) => ({
      text: item.name,
      value: item.id,
    })),
  };
  return filterOptions;
};

const readTagsData = async () => {
  const tags = await DataService.flows.getFilterOptions([TagsEnum.Label, TagsEnum.SoftwareApplication]);
  let filterOptions: GenericFiltersMap<string> = {};
  const tagsGropedByTypeType = groupBy(tags.data, "type");
  for (const [type, value] of ObjectUtils.typedEntries<TagTypes, FlowTagsWithType[]>(tagsGropedByTypeType)) {
    filterOptions[tagToFilterMap[type]!] = value.map((item) => ({ text: item.title, value: item.title }));
  }
  return filterOptions;
};

const goalToFilterMap: { [key in keyof GoalOptions]?: ScheduleFiltersEnum } = {
  objective: ScheduleFiltersEnum.GoalObjectives,
  type: ScheduleFiltersEnum.GoalTypes,
};

const readGoalsData = async () => {
  const options = await DataService.flows.getGoalOptions();
  let filterOptions: GenericFiltersMap<string> = {};

  for (const [goal, type] of ObjectUtils.typedEntries<keyof GoalOptions, ScheduleFiltersEnum>(goalToFilterMap)) {
    filterOptions[type] = options.data[goal].map((item) => ({ text: item.label, value: item.id }));
  }

  return filterOptions;
};

const PriorityLabels: Record<PriorityLevels, string> = {
  [PriorityLevels.none]: "None",
  [PriorityLevels.recommended]: "Recommended",
  [PriorityLevels.important]: "Important",
  [PriorityLevels.required]: "Required",
};

const readPrioritiesData = async () => ({
  [ScheduleFiltersEnum.Priorities]: Object.values(PriorityLevels)
    .filter((value): value is PriorityLevels => typeof value === "number")
    .map((value) => ({
      text: PriorityLabels[value],
      value,
    })),
});

const readStatusesData = async () => ({
  [ScheduleFiltersEnum.Statuses]: Object.values(ScheduleStatusEnum)
    .filter((value): value is ScheduleStatusEnum => typeof value === "number")
    .map((value) => ({
      text: ScheduleStatusEnum[value],
      value,
    })),
});

export const getFilterOptions = async (props: BaseFilterOptionsProps & GetFilterOptionsProps) => {
  const { begin, success, failure } = props;
  const selectedFilters = !props.filters?.length ? defaultFiltersOptions : props.filters;

  const filterHandlers: {
    [key in FiltersToFetch]?: () => Promise<GenericFiltersMap<string>>;
  } = {
    [FiltersToFetch.Packs]: readPacksData,
    [FiltersToFetch.Tags]: readTagsData,
    [FiltersToFetch.Goals]: readGoalsData,
    [FiltersToFetch.Publishers]: readPublishersData,
    [FiltersToFetch.Priorities]: readPrioritiesData,
    [FiltersToFetch.Statuses]: readStatusesData,
  };

  begin();

  const filtersHandlersPromises = Object.values(pick(filterHandlers, selectedFilters)).map((handler) => handler());

  const result = await Promise.allSettled(filtersHandlersPromises);
  let someFulfilled = false;
  let filterOptions: GenericFiltersMap<string> = {};

  result.forEach((item) => {
    if (item.status === PromiseStatuses.fulfilled) {
      someFulfilled = true;
      filterOptions = {
        ...filterOptions,
        ...item.value,
      };
    }
    if (item.status === PromiseStatuses.rejected) {
      failure(item.reason as Error);
    }
  });

  if (someFulfilled) {
    success(filterOptions);
  }
};
